import Mission, { ErrorMission } from 'requests/objects/mission'
import ApiHandler from 'requests/apiHandler'
import EDiffusionAction from 'types/missions/enums/diffusionAction'
import store from 'stores/store'
import { setMessageBar } from 'stores/slices/common'
import { MessageBarType } from '@fluentui/react'

/**
 * MissionsHandler
 * @augments {ApiHandler<Mission, ErrorMission>}
 */
export default class MissionsHandler extends ApiHandler {
    constructor() {
        super({ type: Mission, errorType: ErrorMission, key: 'missions' })
    }

    /**
     * Get all
     * @param {import('axios').AxiosRequestConfig['params']=} params Params
     * @returns {import('requests/apiHandler').RequestApi<Mission[]>} Request
     */
    getAllMap(params = {}) {
        const request = this.initFetchRequest({ url: ['map'], params })
        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(res => /** @type {any} */(res.data[this.objectName])?.map(x => new (this.type)(x)) ?? [])
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * diffuse
     * @param {Mission=} mission mission
     * @param {EDiffusionAction=} actionName actionName
     * @returns {import('requests/apiHandler').RequestApi<Mission>} Request
     */
    diffuse(mission, actionName) {
        const request = this.initFetchRequest({
            url: [mission.missionId, 'diffuse'],
            method: 'POST',
            data: {
                actionName,
            },
        })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => new Mission(res.data[this.objectName]))
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Complete diffusion
     * @param {number=} missionId missionId
     * @returns {import('requests/apiHandler').RequestApi<Mission>} Request
     */
    completeDiffusion(missionId) {
        const request = this.initFetchRequest({ url: [missionId, 'diffuse/complete-diffusion'] })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => new Mission(res.data.mission))
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Refresh diffusion data
     * @param {number=} missionId missionId
     * @returns {import('requests/apiHandler').RequestApi<Mission>} Request
     */
    refreshDiffusion(missionId) {
        const request = this.initFetchRequest({ url: [missionId, 'diffuse/refresh'] })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => new Mission(res.data.mission))
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Cancel diffusion
     * @param {number=} missionId missionId
     * @returns {import('requests/apiHandler').RequestApi<Mission>} Request
     */
    closeDiffusion(missionId) {
        const request = this.initFetchRequest({ url: [missionId, 'diffuse/cancel-diffusion'] })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => new Mission(res.data.mission))
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Backout
     * @param {number} missionId missionId
     * @returns {import('requests/apiHandler').RequestApi<Mission>} Request
     */
    backout(missionId) {
        const request = this.initFetchRequest({ url: [missionId, 'backout'], method: 'PATCH' })

        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(res => new (this.type)(res.data[this.objectName]))
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * diffuse
     * @param {Mission} mission mission
     * @param {number} missionId missionId
     * @returns {import('requests/apiHandler').RequestApi<Mission>} Request
     */
    updateWebsiteInfo(mission, missionId) {
        const request = this.initFetchRequest({ url: [missionId, 'website', 'informations'], method: 'PATCH', data: mission })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: "L'élément a bien été mis à jour" }))
                    // eslint-disable-next-line new-cap
                    return new (this.type)(res.data[this.objectName])
                })
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * diffuse
     * @param {number} missionId missionId
     * @returns {import('requests/apiHandler').RequestApi<Mission>} Request
     */
    diffuseWebsiteInfo(missionId) {
        const request = this.initFetchRequest({ url: [missionId, 'website', 'diffuse'], method: 'PATCH' })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'La mission a bien été diffusé' }))
                    // eslint-disable-next-line new-cap
                    return new (this.type)(res.data[this.objectName])
                })
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * remove
     * @param {number} missionId missionId
     * @returns {import('requests/apiHandler').RequestApi<Mission>} Request
     */
    removeDiffuseWebsiteInfo(missionId) {
        const request = this.initFetchRequest({ url: [missionId, 'website', 'remove'], method: 'DELETE' })

        return this.getRequestApi(
            () => request.fetchRequest
                .then(res => {
                    store.dispatch(setMessageBar({ isDisplayed: true, type: MessageBarType.success, message: 'La mission a bien été supprimée' }))
                    // eslint-disable-next-line new-cap
                    return new (this.type)(res.data[this.objectName])
                })
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }

    /**
     * Get file
     * @param {number} missionId Id
     * @param {number} boardColumnId boardColumnId
     * @returns {import('requests/apiHandler').RequestApi<Blob>} Request
     */
    getExportedItems(missionId, boardColumnId) {
        const request = this.initFetchRequest({
            url: [missionId, 'export', boardColumnId], method: 'GET', responseType: 'arraybuffer',
        })

        return this.getRequestApi(
            () => request.fetchRequest
                // eslint-disable-next-line new-cap
                .then(res => new Blob([res.data]))
                .catch(err => {
                    throw this.handleError(err)
                }),
            request.cancelToken,
        )
    }
}
